import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} MemberRole
 * @property {number} id - Role ID
 * @property {string} name - Role Name
 * @property {string} slug - Role Slug
 */

/**
 * @typedef {object} MemberDetailResponse
 * @property {number} id - Member ID
 * @property {string} tenant_id - Tenant ID
 * @property {number} inst_id - Institution ID
 * @property {string} firstName - First Name
 * @property {string|null} lastName - Last Name
 * @property {string} email - Email Address
 * @property {number} role_id - Role ID
 * @property {string} reg_type - Registration Type
 * @property {string} status - Member Status
 * @property {string} created_at - Created At
 * @property {string} updated_at - Updated At
 * @property {MemberRole} role - Role Object
 * @property {string|null} [teacher_code] - Teacher Code (optional, only for teachers)
 */

/**
 * @typedef {object} GetMemberDetailParams
 * @property {number | string} memberId - Member ID (required)
 */

/**
 * Custom hook to fetch member detail by ID
 */
export const useGetMemberDetailById = () => {
  const [member, setMember] = useState(
    /** @type {MemberDetailResponse | null | undefined} */ (undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch individual member details
   * @async
   * @param {GetMemberDetailParams} params
   */
  const getMemberDetailById = async ({ memberId } = {}) => {
    if (!memberId) {
      setError("Member ID is required");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const url = `${TENANT_DOMAIN}/school/admin/member/edit/${memberId}`;

      const response = await axiosInstance.get(url);

      setMember(response?.data?.data || null);
    } catch (err) {
      setMember(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    member,
    loading,
    error,
    getMemberDetailById,
    setMember,
  };
};
